import createDOMPurify from "dompurify";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { marked } from "marked";
import { GITHUB } from "../constants";
import { addH2SequencesToSections } from "./markdown-section-h2";
import {
  isPathAccessible,
  shouldIncludeFolderInList,
  sortFoldersForDisplay,
} from "./private-folder-access";

const FOLDER_LIST_TOKEN_DETECT_REGEX = /\{\{\s*folderList\s*\}\}/i;
const FOLDER_LIST_TOKEN_REPLACE_REGEX = /\{\{\s*folderList\s*\}\}/gi;
const REPO_CONTENTS_API_BASE =
  "https://api.github.com/repos/alialiayman/reflections/contents";
const FOLDER_LIST_FALLBACK = "⚠️ تعذر تحميل قائمة المجلدات من GitHub حالياً.";
const FOLDER_SUBTITLE_TOKEN = "[[FOLDER_SUBTITLE]]";
const FOLDER_SUBTITLE_TOKEN_REGEX_GLOBAL = /\[\[\s*FOLDER_SUBTITLE\s*\]\]?/gi;

const CONTAINER_XML = `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`;

const DEFAULT_EPUB_METADATA = {
  creator: "ايمن علي محمد",
  publisher: "نشر مستقل عبر الإنترنت",
  subject: "تأملات روحية",
  rights: "© جميع الحقوق محفوظة",
  language: "ar",
  contactWhatsApp: "0019495221879",
  contactUrl: "https://wa.me/19495221879",
  website: "https://a-reflections.web.app",
  location: "مدينة الرحاب في القاهرة",
  disclaimer:
    "هذا الكتاب تأملي/تجريبي، ويعبّر عن تجربة شخصية، وليس مرجعًا طبيًا أو قانونيًا أو مهنيًا.",
  aiDisclosure:
    "تم إنشاء وتحرير أجزاء من هذا الكتاب باستخدام تقنيات الذكاء الاصطناعي. الفكرة أصلًا من الكاتب، بينما الصياغة اللغوية في معظمها مولَّدة بالذكاء الاصطناعي، كما أُنشئت الصور كذلك بالذكاء الاصطناعي.",
};

const DEFAULT_SECTION_MIDDLE_IMAGE_RATIO = 0.3;

const escapeXml = (value = "") =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");

const slugify = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-") || "reflections";

const getNumericPrefix = (fileName = "") => {
  const match = fileName.match(/^(\d+(?:[-.]\d+)*)/);
  if (!match) {
    return null;
  }

  const segments = match[1]
    .split(/[-.]/)
    .map((segment) => Number.parseInt(segment, 10));

  if (segments.length === 0 || segments.some((segment) => Number.isNaN(segment))) {
    return null;
  }

  return {
    sectionMajor: segments[0],
    segments,
    raw: match[1],
  };
};

const compareNumericPrefix = (left, right) => {
  if (!left && !right) {
    return 0;
  }
  if (!left) {
    return 1;
  }
  if (!right) {
    return -1;
  }

  const maxLength = Math.max(left.segments.length, right.segments.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftSegment = left.segments[index];
    const rightSegment = right.segments[index];

    if (leftSegment === undefined) {
      return -1;
    }
    if (rightSegment === undefined) {
      return 1;
    }
    if (leftSegment !== rightSegment) {
      return leftSegment - rightSegment;
    }
  }

  return 0;
};

const splitMarkdownSections = (markdownText) => {
  const lines = markdownText.split("\n");
  const sections = [];
  let currentHeading = "";
  let currentLines = [];

  const pushSection = () => {
    if (currentLines.length === 0) {
      return;
    }

    sections.push({
      heading: currentHeading || "Introduction",
      markdown: currentLines.join("\n").trim(),
    });
  };

  lines.forEach((line) => {
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);

    if (headingMatch) {
      pushSection();
      currentHeading = headingMatch[2].trim() || "Untitled";
      currentLines = [line];
    } else {
      currentLines.push(line);
    }
  });

  pushSection();

  if (sections.length === 0) {
    return [
      {
        heading: "Introduction",
        markdown: markdownText,
      },
    ];
  }

  return sections;
};

const mapImagesToSections = (images, sectionsCount) => {
  if (sectionsCount === 0) {
    return [];
  }

  const normalized = images
    .map((image) => ({
      ...image,
      numericPrefix: getNumericPrefix(image.name),
    }))
    .sort((left, right) => {
      const numericOrder = compareNumericPrefix(
        left.numericPrefix,
        right.numericPrefix
      );
      if (numericOrder !== 0) {
        return numericOrder;
      }
      return left.name.localeCompare(right.name);
    });

  let fallbackSection = 0;

  return normalized.map((image) => {
    const prefix = image.numericPrefix;

    if (!prefix) {
      const sectionIndex = Math.min(fallbackSection, sectionsCount - 1);
      fallbackSection += 1;
      return { ...image, sectionIndex };
    }

    const safeTarget = Math.max(
      0,
      Math.min(prefix.sectionMajor - 1, sectionsCount - 1)
    );

    return {
      ...image,
      sectionIndex: safeTarget,
    };
  });
};

const getImageCaption = (image, imageCaptionMap = null) => {
  const base =
    stripLeadingImageNumber(fileNameWithoutExtension(image.name)) ||
    fileNameWithoutExtension(image.name);
  if (!imageCaptionMap) {
    return base;
  }
  return imageCaptionMap[image.name] || imageCaptionMap[base] || base;
};

const buildSectionImageFigureHtml = (image, imageCaptionMap = null) =>
  `<figure style="text-align:center;"><img src="../images/${image.fileName}" alt="${escapeXml(
    image.name
  )}" style="border-radius:12px;border:3px solid #00BFA6;box-shadow:0 4px 20px rgba(0,191,166,0.35),0 8px 30px rgba(0,0,0,0.25);" /><figcaption style="text-align:center;margin-top:0.35rem;font-size:0.85em;opacity:0.7;">${escapeXml(
    getImageCaption(image, imageCaptionMap)
  )}</figcaption></figure>`;

const distributeImagesInSection = (
  images,
  middleRatio = DEFAULT_SECTION_MIDDLE_IMAGE_RATIO
) => {
  if (images.length <= 1) {
    return {
      middle: [],
      end: images,
    };
  }

  const normalizedRatio = clamp(middleRatio, 0, 1);
  const rawMiddleCount = Math.round(images.length * normalizedRatio);
  const middleCount = clamp(rawMiddleCount, 1, images.length - 1);

  return {
    middle: images.slice(0, middleCount),
    end: images.slice(middleCount),
  };
};

const injectHtmlAtMarkdownMidpoint = (markdownText, htmlBlock) => {
  if (!htmlBlock) {
    return markdownText;
  }

  const lines = markdownText.split("\n");
  const breakIndexes = [];

  for (let index = 1; index < lines.length - 1; index += 1) {
    if (
      lines[index].trim() === "" &&
      lines[index - 1].trim() !== "" &&
      lines[index + 1].trim() !== ""
    ) {
      breakIndexes.push(index);
    }
  }

  if (breakIndexes.length === 0) {
    return `${markdownText}\n\n${htmlBlock}`;
  }

  const targetBreakIndex = breakIndexes[Math.floor(breakIndexes.length / 2)];
  const before = lines.slice(0, targetBreakIndex + 1).join("\n");
  const after = lines.slice(targetBreakIndex + 1).join("\n");

  return `${before}\n${htmlBlock}\n\n${after}`;
};

const ensureXhtmlVoidTags = (html = "") => {
  const voidTags = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
  ]);

  return html.replace(/<([a-zA-Z][\w:-]*)([^>]*)>/g, (match, tagName, attrs) => {
    const normalizedTagName = tagName.toLowerCase();
    if (!voidTags.has(normalizedTagName)) {
      return match;
    }

    const trimmedAttrs = attrs.trimEnd();
    if (trimmedAttrs.endsWith("/")) {
      return `<${tagName}${attrs}>`;
    }

    return `<${tagName}${attrs} />`;
  });
};

const isRtlLanguage = (language = "") => String(language).toLowerCase() === "ar";

const buildXhtml = (title, body, { language = "ar", dir = "rtl" } = {}) => `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="${escapeXml(
  language
)}" xml:lang="${escapeXml(language)}" dir="${escapeXml(dir)}">
  <head>
    <meta charset="utf-8" />
    <title>${escapeXml(title)}</title>
  </head>
  <body style="text-align:${dir === "rtl" ? "right" : "left"};">
${body}
  </body>
</html>`;

const fileExtension = (fileName = "") => {
  const extension = fileName.split(".").pop();
  return extension ? extension.toLowerCase() : "jpg";
};

const fileNameWithoutExtension = (fileName = "") =>
  fileName.replace(/\.[^/.]+$/, "");

const normalizeImageLabel = (fileName = "") =>
  fileNameWithoutExtension(fileName)
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const stripLeadingImageNumber = (label = "") =>
  label.replace(/^\d+(?:[-.]\d+)*[\s._-]*/, "").trim();

const extractFirstMarkdownHeader = (markdownText = "") => {
  const lines = markdownText.split("\n");
  for (const line of lines) {
    const headingMatch = line.match(/^#{1,6}\s+(.*)$/);
    if (headingMatch && headingMatch[1].trim()) {
      return headingMatch[1].trim();
    }
  }

  return null;
};

const extractFirstMarkdownParagraph = (markdownText = "") => {
  const lines = markdownText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const paragraphLines = [];
  for (const line of lines) {
    if (line.startsWith("#")) {
      continue;
    }

    if (/^[-*]|^\d+[.)]/.test(line)) {
      if (paragraphLines.length > 0) {
        break;
      }
      continue;
    }

    paragraphLines.push(line);
    if (paragraphLines.length >= 3) {
      break;
    }
  }

  return paragraphLines.join(" ").trim() || "";
};

const imageMimeTypeFromName = (fileName = "") => {
  const extension = fileExtension(fileName);
  const extensionToMime = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
    svg: "image/svg+xml",
  };

  return extensionToMime[extension] || "image/jpeg";
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const resolveSectionImageMiddleRatio = (
  explicitRatio = null,
  defaultRatio = DEFAULT_SECTION_MIDDLE_IMAGE_RATIO
) => {
  if (typeof explicitRatio === "number" && !Number.isNaN(explicitRatio)) {
    return clamp(explicitRatio, 0, 1);
  }

  const query = window.location.search;
  const match = query.match(/(?:\?|&)epubImageMiddleRatio=([^&]+)/i);

  if (!match) {
    return clamp(defaultRatio, 0, 1);
  }

  const parsed = Number.parseFloat(decodeURIComponent(match[1]));
  if (Number.isNaN(parsed)) {
    return clamp(defaultRatio, 0, 1);
  }

  const ratio = parsed > 1 ? parsed / 100 : parsed;
  return clamp(ratio, 0, 1);
};

const getTargetTranslationLanguage = () => {
  const query = window.location.search;
  const match = query.match(/(?:\?|&)_x_tr_tl=([^&]+)/i);

  if (!match?.[1]) {
    return "";
  }

  return decodeURIComponent(match[1]).trim().toLowerCase();
};

const shouldUseTranslatedMarkdown = () => {
  const targetLanguage = getTargetTranslationLanguage();
  return (
    window.location.hostname.includes("translate.goog") &&
    Boolean(targetLanguage) &&
    targetLanguage !== "ar"
  );
};

const buildTranslateProxyUrl = (sourceUrl, targetLanguage) => {
  const parsed = new URL(sourceUrl);
  const translatedHost = `${parsed.hostname
    .replaceAll("-", "--")
    .replaceAll(".", "-")}.translate.goog`;
  const translatedUrl = new URL(
    `https://${translatedHost}${parsed.pathname}${parsed.search}`
  );

  translatedUrl.searchParams.set("_x_tr_sl", "ar");
  translatedUrl.searchParams.set("_x_tr_tl", targetLanguage);
  translatedUrl.searchParams.set("_x_tr_hl", targetLanguage);
  translatedUrl.searchParams.set("_x_tr_pto", "wapp");

  return translatedUrl.toString();
};

const resolveEpubLanguage = () => {
  const translatedLanguage = getTargetTranslationLanguage();
  if (translatedLanguage && translatedLanguage !== "ar") {
    return translatedLanguage;
  }

  return DEFAULT_EPUB_METADATA.language;
};

const safelyDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getNormalizedPathSegments = (path = "") =>
  path
    .split("/")
    .filter(Boolean)
    .map((segment) => safelyDecodeURIComponent(segment).trim())
    .filter(Boolean);

const toEncodedRepoPath = (path = "") => {
  const segments = getNormalizedPathSegments(path);
  if (!segments.length) {
    return "";
  }
  return segments.map((segment) => encodeURIComponent(segment)).join("/");
};

const shouldIncludeFolder = (folder, githubLogin = "") =>
  shouldIncludeFolderInList(folder, githubLogin);

const isSupportedImage = (item) =>
  item?.type === "file" && /\.(?:gif|jpe?g|png|svg|webp)$/i.test(item.name || "");

const joinRepoPath = (parentPath, childName) =>
  [...getNormalizedPathSegments(parentPath), childName.trim()].join("/");

const fetchFolderContents = async (path) => {
  const encodedPath = toEncodedRepoPath(path);
  const folderApi = encodedPath
    ? `${REPO_CONTENTS_API_BASE}/${encodedPath}?ref=main`
    : `${REPO_CONTENTS_API_BASE}?ref=main`;
  const response = await fetch(folderApi);
  if (!response.ok) {
    throw new Error(`Unable to discover nested EPUB pages under ${path || "/"}`);
  }

  const contents = await response.json();
  if (!Array.isArray(contents)) {
    throw new Error(`Invalid folder listing for ${path || "/"}`);
  }
  return contents;
};

const collectDescendantPages = async (parentPath, knownContents = null, githubLogin = "") => {
  const contents = knownContents || (await fetchFolderContents(parentPath));
  const childFolders = sortFoldersForDisplay(
    contents.filter((item) => item.type === "dir" && shouldIncludeFolder(item, githubLogin))
  );
  const pages = [];

  for (const folder of childFolders) {
    const childPath = joinRepoPath(parentPath, folder.name);
    const childContents = await fetchFolderContents(childPath);
    const readme = childContents.find(
      (item) => item.type === "file" && item.name.toLowerCase() === "readme.md"
    );

    if (readme) {
      pages.push({
        path: childPath,
        markdown: await fetchMarkdown(childPath),
        images: childContents.filter(isSupportedImage).map((image) => ({
          name: image.name,
          url: image.download_url,
        })),
      });
    }

    pages.push(...(await collectDescendantPages(childPath, childContents, githubLogin)));
  }

  return pages;
};

const toDisplayTitle = (folderName = "") =>
  folderName.replace(/^\s*\d+\s*[-_.]?\s*/, "").trim();

const buildFolderUrl = (currentPath, folderName) => {
  const normalizedParentPath = getNormalizedPathSegments(currentPath)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  const folderSlug = encodeURIComponent(folderName.trim());
  if (!normalizedParentPath) {
    return `https://a-reflections.web.app/${folderSlug}`;
  }
  return `https://a-reflections.web.app/${normalizedParentPath}/${folderSlug}`;
};

const buildFolderReadmeUrl = (currentPath, folderName) => {
  const encodedPath = toEncodedRepoPath(currentPath);
  const encodedFolderName = encodeURIComponent(folderName.trim());
  const relativeReadmePath = encodedPath
    ? `${encodedPath}/${encodedFolderName}/README.md`
    : `${encodedFolderName}/README.md`;
  return `${GITHUB}/${relativeReadmePath}`;
};

const extractFirstReadmeLine = (markdownText) => {
  if (typeof markdownText !== "string") {
    return "";
  }
  const firstLine = markdownText
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  if (!firstLine) {
    return "";
  }
  return firstLine
    .replace(/^#{1,6}\s*/, "")
    .replace(/^[-*+]\s+/, "")
    .replace(/^\d+\.\s+/, "")
    .replace(/\|/g, "\\|")
    .trim();
};

const fetchFolderSubtitles = async (folders, currentPath) => {
  return Promise.all(
    folders.map(async (folder) => {
      try {
        const readmeUrl = buildFolderReadmeUrl(currentPath, folder.name);
        const response = await fetch(readmeUrl);
        if (!response.ok) {
          return { ...folder, subtitle: "" };
        }
        const text = await response.text();
        return { ...folder, subtitle: extractFirstReadmeLine(text) };
      } catch {
        return { ...folder, subtitle: "" };
      }
    })
  );
};

const buildFolderTableMarkdown = (folders, currentPath) => {
  const links = sortFoldersForDisplay(folders).map((folder) => {
    const title = `[${toDisplayTitle(folder.name)}](${buildFolderUrl(
      currentPath,
      folder.name
    )})`;
    if (!folder.subtitle) {
      return title;
    }
    return `${title} ${FOLDER_SUBTITLE_TOKEN} ${folder.subtitle}`;
  });
  const tableLines = ["|   |   |   |", "| --- | --- | --- |"];
  for (let index = 0; index < links.length; index += 3) {
    const first = links[index] || " ";
    const second = links[index + 1] || " ";
    const third = links[index + 2] || " ";
    tableLines.push(`| ${first} | ${second} | ${third} |`);
  }
  return tableLines.join("\n");
};

const hasFolderListToken = (markdownText) =>
  FOLDER_LIST_TOKEN_DETECT_REGEX.test(markdownText);

const replaceFolderListToken = (markdownText, folders, currentPath) => {
  if (!hasFolderListToken(markdownText)) {
    return markdownText;
  }
  const tableMarkdown = buildFolderTableMarkdown(folders, currentPath);
  return markdownText.replace(FOLDER_LIST_TOKEN_REPLACE_REGEX, tableMarkdown);
};

const replaceFolderListWithFallback = (markdownText) => {
  if (!hasFolderListToken(markdownText)) {
    return markdownText;
  }
  return markdownText.replace(FOLDER_LIST_TOKEN_REPLACE_REGEX, FOLDER_LIST_FALLBACK);
};

const expandFolderListToken = async (markdownText, path, githubLogin = "") => {
  if (!hasFolderListToken(markdownText)) {
    return markdownText;
  }
  const encodedPath = toEncodedRepoPath(path);
  const folderApi = encodedPath
    ? `${REPO_CONTENTS_API_BASE}/${encodedPath}?ref=main`
    : `${REPO_CONTENTS_API_BASE}?ref=main`;
  try {
    const response = await fetch(folderApi);
    if (!response.ok) {
      return replaceFolderListWithFallback(markdownText);
    }
    const data = await response.json();
    if (!Array.isArray(data)) {
      return replaceFolderListWithFallback(markdownText);
    }
    const folders = data.filter(
      (item) => item.type === "dir" && shouldIncludeFolder(item, githubLogin)
    );
    const foldersWithSubtitles = await fetchFolderSubtitles(folders, path);
    return replaceFolderListToken(markdownText, foldersWithSubtitles, path);
  } catch {
    return replaceFolderListWithFallback(markdownText);
  }
};

const normalizeFolderTableHtml = (html) => {
  if (typeof html !== "string" || !html.includes("<table")) {
    return html;
  }
  let out = html
    .replace(/<table>/g, '<table class="readme-folder-table">')
    .replace(/<th>/g, '<th class="readme-folder-table-th">');

  out = out.replace(/<td([^>]*)>([\s\S]*?)<\/td>/g, (_, attrs = "", inner = "") => {
    const cleanedInner = String(inner).replace(FOLDER_SUBTITLE_TOKEN_REGEX_GLOBAL, FOLDER_SUBTITLE_TOKEN);
    const tokenIndex = cleanedInner.indexOf(FOLDER_SUBTITLE_TOKEN);
    if (tokenIndex < 0) {
      return `<td class="readme-folder-table-td"${attrs}>${cleanedInner}</td>`;
    }
    const before = cleanedInner.slice(0, tokenIndex).trim();
    const after = cleanedInner.slice(tokenIndex + FOLDER_SUBTITLE_TOKEN.length).trim();
    return `<td class="readme-folder-table-td"${attrs}><div class="folder-cell-title">${before}</div><div class="folder-cell-subtitle">${after}</div></td>`;
  });
  return out;
};

const fetchMarkdown = async (path, githubLogin = "") => {
  if (!isPathAccessible(path, githubLogin)) {
    throw new Error("Unable to fetch markdown for EPUB export");
  }

  const normalizedPath = path === "/" ? "" : path;
  const readmeUrl = `${GITHUB}${normalizedPath.endsWith("/") ? normalizedPath : `${normalizedPath}/`}README.md`;
  if (shouldUseTranslatedMarkdown()) {
    try {
      const translatedReadmeUrl = buildTranslateProxyUrl(
        readmeUrl,
        getTargetTranslationLanguage()
      );
      const translatedResponse = await fetch(translatedReadmeUrl);
      if (translatedResponse.ok) {
        return translatedResponse.text();
      }
    } catch {
      // Fallback to the original markdown source below.
    }
  }

  const response = await fetch(readmeUrl);

  if (!response.ok) {
    throw new Error("Unable to fetch markdown for EPUB export");
  }

  const markdownText = await response.text();
  return expandFolderListToken(markdownText, normalizedPath, githubLogin);
};

const resolveDefaultTitleFromPath = (path = "") =>
  path === "/"
    ? "تأملات"
    : decodeURIComponent(path.replaceAll("/", "").trim()) || "تأملات";

const buildSectionPreviewFigureHtml = (image, imageCaptionMap = null) =>
  `<figure style="text-align:center;"><img src="${escapeXml(
    image.url
  )}" alt="${escapeXml(image.name)}" style="border-radius:12px;border:3px solid #00BFA6;box-shadow:0 4px 20px rgba(0,191,166,0.35),0 8px 30px rgba(0,0,0,0.25);" /><figcaption style="text-align:center;margin-top:0.35rem;font-size:0.85em;opacity:0.7;">${escapeXml(
    getImageCaption(image, imageCaptionMap)
  )}</figcaption></figure>`;

/** In-app reading preview only — prefix first `##` like README text view; never written to repo or EPUB files */
const prefixFirstH2LineWithNumberForPreview = (markdown, n) => {
  if (n == null || !Number.isInteger(n) || typeof markdown !== "string") {
    return markdown;
  }
  return markdown.replace(/^## (?!#)([^\r\n]*)$/m, (_, rest) => {
    const body = rest.replace(/^\s+/, "");
    return `## ${n}. ${body}`;
  });
};

export const buildEpubLikePreview = async ({
  path,
  images,
  sectionImageMiddleRatio,
  sourceMarkdown,
  imageCaptionMap,
  githubLogin = "",
}) => {
  const markdown =
    typeof sourceMarkdown === "string" && sourceMarkdown.trim()
      ? sourceMarkdown
      : await fetchMarkdown(path, githubLogin);
  const sections = addH2SequencesToSections(splitMarkdownSections(markdown));
  const firstHeaderTitle = extractFirstMarkdownHeader(markdown);
  const resolvedSectionImageMiddleRatio = resolveSectionImageMiddleRatio(
    sectionImageMiddleRatio
  );

  const coverImage = images.find(
    (image) => normalizeImageLabel(image.name) === "cover"
  );
  const backCoverImage = images.find(
    (image) => normalizeImageLabel(image.name) === "back cover"
  );

  const contentImages = images.filter(
    (image) => image !== coverImage && image !== backCoverImage
  );

  const mappedImages = mapImagesToSections(contentImages, sections.length);
  const imagesBySection = new Map();

  mappedImages.forEach((image) => {
    const current = imagesBySection.get(image.sectionIndex) || [];
    imagesBySection.set(image.sectionIndex, [...current, image]);
  });

  const title = firstHeaderTitle || resolveDefaultTitleFromPath(path);
  const domPurify = createDOMPurify(window);

  const sectionPreviews = sections.map((section, index) => {
    const sectionImages = imagesBySection.get(index) || [];
    const distributedImages = distributeImagesInSection(
      sectionImages,
      resolvedSectionImageMiddleRatio
    );
    const middleImagesHtml = distributedImages.middle
      .map((image) => buildSectionPreviewFigureHtml(image, imageCaptionMap))
      .join("\n");
    const endImagesHtml = distributedImages.end
      .map((image) => buildSectionPreviewFigureHtml(image, imageCaptionMap))
      .join("\n");

    const markdownForPreview = prefixFirstH2LineWithNumberForPreview(
      section.markdown,
      section.h2Sequence
    );

    const markdownWithMiddleImages = injectHtmlAtMarkdownMidpoint(
      markdownForPreview,
      middleImagesHtml
    );

    const sectionHtml = normalizeFolderTableHtml(
      domPurify.sanitize(marked.parse(markdownWithMiddleImages, { xhtml: true }))
    );

    return {
      heading: section.heading,
      html: `${sectionHtml}${endImagesHtml ? `\n${endImagesHtml}` : ""}`,
    };
  });

  return {
    title,
    sections: sectionPreviews,
  };
};

const fetchImageBlob = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to fetch image: ${url}`);
  }
  return response.blob();
};

const getFolderNameFromPath = (path = "") => {
  if (!path || path === "/") {
    return "reflections";
  }

  const segments = path.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] || "reflections";
  return decodeURIComponent(lastSegment);
};

const padNumber = (value) => String(value).padStart(2, "0");

const generateIsbnLikeIdentifier = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = padNumber(now.getMonth() + 1);
  const day = padNumber(now.getDate());
  const hour = padNumber(now.getHours());
  const minute = padNumber(now.getMinutes());
  const second = padNumber(now.getSeconds());
  const randomPart = String(Math.floor(Math.random() * 1000)).padStart(3, "0");

  return `979-${year}-${month}${day}-${hour}${minute}${second}-${randomPart}`;
};

export const exportFolderToEpub = async ({
  path,
  images,
  sectionImageMiddleRatio,
  sourceMarkdown,
  language,
  imageCaptionMap,
  githubLogin = "",
}) => {
  if (!isPathAccessible(path, githubLogin)) {
    throw new Error("Unable to export this folder.");
  }

  const rootMarkdown =
    typeof sourceMarkdown === "string" && sourceMarkdown.trim()
      ? await expandFolderListToken(sourceMarkdown, path, githubLogin)
      : await fetchMarkdown(path, githubLogin);
  const descendantPages = await collectDescendantPages(path, null, githubLogin);
  const pages = [
    { path, markdown: rootMarkdown, images: Array.isArray(images) ? images : [] },
    ...descendantPages,
  ];
  const sections = pages.flatMap((page, pageIndex) =>
    splitMarkdownSections(page.markdown).map((section, pageSectionIndex) => ({
      ...section,
      pageIndex,
      pageSectionIndex,
    }))
  );
  const firstHeaderTitle = extractFirstMarkdownHeader(rootMarkdown);
  const resolvedSectionImageMiddleRatio = resolveSectionImageMiddleRatio(
    sectionImageMiddleRatio
  );

  const rootImages = pages[0].images;
  const coverImage = rootImages.find(
    (image) => normalizeImageLabel(image.name) === "cover"
  );
  const backCoverImage = rootImages.find(
    (image) => normalizeImageLabel(image.name) === "back cover"
  );

  let sectionOffset = 0;
  const mappedImages = pages.flatMap((page, pageIndex) => {
    const pageSectionsCount = sections.filter(
      (section) => section.pageIndex === pageIndex
    ).length;
    const pageImages = page.images.filter(
      (image) => image !== coverImage && image !== backCoverImage
    );
    const mappedPageImages = mapImagesToSections(pageImages, pageSectionsCount).map(
      (image) => ({ ...image, sectionIndex: image.sectionIndex + sectionOffset })
    );
    sectionOffset += pageSectionsCount;
    return mappedPageImages;
  });
  const domPurify = createDOMPurify(window);

  const enrichedImages = await Promise.all(
    mappedImages.map(async (image, index) => {
      const blob = await fetchImageBlob(image.url);
      const extension = fileExtension(image.name);
      const id = `image-${index + 1}`;
      const fileName = `${id}.${extension}`;
      const mediaType = imageMimeTypeFromName(image.name) || blob.type;

      return {
        ...image,
        id,
        fileName,
        mediaType,
        blob,
      };
    })
  );

  const imagesBySection = new Map();
  enrichedImages.forEach((image) => {
    const current = imagesBySection.get(image.sectionIndex) || [];
    imagesBySection.set(image.sectionIndex, [...current, image]);
  });

  const title = firstHeaderTitle || resolveDefaultTitleFromPath(path);
  const identifier = generateIsbnLikeIdentifier();
  const description = extractFirstMarkdownParagraph(rootMarkdown);
  const createdDate = new Date().toISOString().slice(0, 10);
  const modifiedDate = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
  const copyrightYear = new Date().getFullYear();
  const epubMetadata = {
    ...DEFAULT_EPUB_METADATA,
    language:
      typeof language === "string" && language.trim()
        ? language.trim().toLowerCase()
        : resolveEpubLanguage(),
    rights: `© ${copyrightYear} ${DEFAULT_EPUB_METADATA.creator}. جميع الحقوق محفوظة.`,
    description,
  };
  const xhtmlDir = isRtlLanguage(epubMetadata.language) ? "rtl" : "ltr";
  const xhtmlOptions = { language: epubMetadata.language, dir: xhtmlDir };

  const coverAsset = coverImage
    ? (() => {
        const extension = fileExtension(coverImage.name);
        return {
          ...coverImage,
          id: "cover-image",
          fileName: `cover.${extension}`,
          mediaType: imageMimeTypeFromName(coverImage.name),
        };
      })()
    : null;

  const backCoverAsset = backCoverImage
    ? (() => {
        const extension = fileExtension(backCoverImage.name);
        return {
          ...backCoverImage,
          id: "back-cover-image",
          fileName: `back-cover.${extension}`,
          mediaType: imageMimeTypeFromName(backCoverImage.name),
        };
      })()
    : null;

  const [coverBlob, backCoverBlob] = await Promise.all([
    coverAsset ? fetchImageBlob(coverAsset.url) : Promise.resolve(null),
    backCoverAsset ? fetchImageBlob(backCoverAsset.url) : Promise.resolve(null),
  ]);

  const chapterDocs = sections.map((section, index) => {
    const chapterId = `chapter-${index + 1}`;
    const chapterHref = `text/${chapterId}.xhtml`;
    const sectionImages = imagesBySection.get(index) || [];
    const distributedImages = distributeImagesInSection(
      sectionImages,
      resolvedSectionImageMiddleRatio
    );
    const middleImagesHtml = distributedImages.middle
      .map((image) => buildSectionImageFigureHtml(image, imageCaptionMap))
      .join("\n");
    const endImagesHtml = distributedImages.end
      .map((image) => buildSectionImageFigureHtml(image, imageCaptionMap))
      .join("\n");
    const markdownWithMiddleImages = injectHtmlAtMarkdownMidpoint(
      section.markdown,
      middleImagesHtml
    );
    const sectionHtml = normalizeFolderTableHtml(
      domPurify.sanitize(marked.parse(markdownWithMiddleImages, { xhtml: true }))
    );
    const sectionXhtml = ensureXhtmlVoidTags(sectionHtml);

    const body = `    <section id="${chapterId}">\n      ${sectionXhtml}\n      ${endImagesHtml}\n    </section>`;

    return {
      id: chapterId,
      href: chapterHref,
      heading: section.heading,
      xhtml: buildXhtml(section.heading, body, xhtmlOptions),
    };
  });

  const coverTopSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" role="img" aria-label="زخرفة غلاف" style="max-width:220px;width:100%;height:auto;display:block;margin:0 auto 1rem auto;"><defs><linearGradient id="coverGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#2b6651"/><stop offset="100%" stop-color="#1b4d3e"/></linearGradient></defs><rect x="10" y="10" width="300" height="160" rx="20" fill="url(#coverGrad)" opacity="0.12"/><circle cx="160" cy="54" r="20" fill="#d8b464" opacity="0.85"/><path d="M72 120c22-16 44-16 66 0v26c-22-16-44-16-66 0z" fill="#1b4d3e" opacity="0.95"/><path d="M182 120c22-16 44-16 66 0v26c-22-16-44-16-66 0z" fill="#1b4d3e" opacity="0.95"/><path d="M138 120c7-4 14-6 22-6s15 2 22 6v26c-7-4-14-6-22-6s-15 2-22 6z" fill="#2b6651"/><path d="M72 120c22-16 44-16 66 0M182 120c22-16 44-16 66 0" fill="none" stroke="#d8b464" stroke-width="2" opacity="0.9"/></svg>`;

  const coverDoc = coverAsset
    ? {
        id: "cover-page",
        href: "text/cover-page.xhtml",
        heading: "الغلاف",
        xhtml: buildXhtml(
          "الغلاف",
          `    <section id="cover" style="text-align:center;min-height:100vh;display:flex;flex-direction:column;justify-content:center;align-items:center;box-sizing:border-box;padding:1rem;"><img src="../images/${coverAsset.fileName}" alt="${escapeXml(
            title
          )}" style="max-width:100%;height:auto;border-radius:12px;border:3px solid #00BFA6;box-shadow:0 4px 20px rgba(0,191,166,0.35),0 8px 30px rgba(0,0,0,0.25);" /></section>`,
          xhtmlOptions
        ),
      }
    : null;

  const titlePageDoc = {
    id: "title-page",
    href: "text/title-page.xhtml",
    heading: title,
    xhtml: buildXhtml(
      title,
      `    <section id="title-page" style="text-align:center;min-height:100vh;display:flex;flex-direction:column;justify-content:center;align-items:center;box-sizing:border-box;padding:2rem 1rem;">${coverTopSvg}<h1 style="margin:1.5rem 0 0.75rem 0;font-size:1.6em;line-height:1.4;">${escapeXml(
        title
      )}</h1><p style="margin:0.5rem 0;font-size:1.1em;opacity:0.85;">${escapeXml(
        epubMetadata.creator
      )}</p><p style="margin:0.5rem 0;font-size:0.8em;opacity:0.6;">${escapeXml(
        epubMetadata.publisher
      )}</p></section>`,
      xhtmlOptions
    ),
  };

  const infoDoc = {
    id: "book-info-page",
    href: "text/book-info-page.xhtml",
    heading: "معلومات الكتاب",
    xhtml: buildXhtml(
      "معلومات الكتاب",
      `    <section id="book-info" style="max-width:640px;margin:0 auto;padding:2rem 1rem;line-height:1.8;font-size:0.85em;"><p><strong>العنوان الكامل:</strong> ${escapeXml(
        title
      )}</p><p><strong>المؤلف:</strong> ${escapeXml(
        epubMetadata.creator
      )}</p><p><strong>الناشر:</strong> ${escapeXml(
        epubMetadata.publisher
      )}</p><p><strong>رقم الكتاب:</strong> ${escapeXml(
        identifier
      )}</p><p><strong>الموقع:</strong> ${escapeXml(
        epubMetadata.location
      )}</p><p><strong>وسيلة التواصل (واتساب):</strong> ${escapeXml(
        epubMetadata.contactWhatsApp
      )}</p><p><strong>رابط التواصل:</strong> <a href="${escapeXml(
        epubMetadata.contactUrl
      )}">${escapeXml(epubMetadata.contactUrl)}</a></p><p><strong>الموقع الإلكتروني:</strong> ${escapeXml(
        epubMetadata.website
      )}</p><p><strong>حقوق النشر:</strong> ${escapeXml(
        epubMetadata.rights
      )}</p><p><strong>تنويه الذكاء الاصطناعي:</strong> ${escapeXml(
        epubMetadata.aiDisclosure
      )}</p><p><strong>إخلاء المسؤولية:</strong> ${escapeXml(
        epubMetadata.disclaimer
      )}</p><p><strong>تاريخ الإنشاء:</strong> ${escapeXml(createdDate)}</p></section>`,
      xhtmlOptions
    ),
  };

  const backCoverDoc = backCoverAsset
    ? {
        id: "back-cover-page",
        href: "text/back-cover-page.xhtml",
        heading: "الغلاف الخلفي",
        xhtml: buildXhtml(
          "الغلاف الخلفي",
          `    <section id="back-cover" style="text-align:center;"><img src="../images/${backCoverAsset.fileName}" alt="الغلاف الخلفي" style="max-width:100%;height:auto;" /></section>`,
          xhtmlOptions
        ),
      }
    : null;

  const spineDocs = [
    ...(coverDoc ? [coverDoc] : []),
    titlePageDoc,
    infoDoc,
    ...chapterDocs,
    ...(backCoverDoc ? [backCoverDoc] : []),
  ];

  const navBody = `    <nav epub:type="toc" id="toc">\n      <h1>${escapeXml(
    title
  )}</h1>\n      <ol>\n${spineDocs
    .map(
      (chapter) =>
        `        <li><a href="${chapter.href}">${escapeXml(chapter.heading)}</a></li>`
    )
    .join("\n")}\n      </ol>\n    </nav>`;
  const navXhtml = buildXhtml(`${title} - الفهرس`, navBody, xhtmlOptions);
  const tocNcx = `<?xml version="1.0" encoding="UTF-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1" xml:lang="${escapeXml(
    epubMetadata.language
  )}">
  <head>
    <meta name="dtb:uid" content="${escapeXml(identifier)}"/>
    <meta name="dtb:depth" content="1"/>
    <meta name="dtb:totalPageCount" content="0"/>
    <meta name="dtb:maxPageNumber" content="0"/>
  </head>
  <docTitle>
    <text>${escapeXml(title)}</text>
  </docTitle>
  <navMap>
    ${spineDocs
      .map(
        (chapter, index) => `
    <navPoint id="navPoint-${index + 1}" playOrder="${index + 1}">
      <navLabel><text>${escapeXml(chapter.heading)}</text></navLabel>
      <content src="${chapter.href}"/>
    </navPoint>`
      )
      .join("")}
  </navMap>
</ncx>`;

  const manifestItems = [
    '<item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>',
    '<item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>',
    ...spineDocs.map(
      (chapter) => {
        const chapterProperties = chapter.id === "title-page" ? ' properties="svg"' : "";
        return `<item id="${chapter.id}" href="${chapter.href}" media-type="application/xhtml+xml"${chapterProperties}/>`;
      }
    ),
    ...(coverAsset
      ? [
          `<item id="${coverAsset.id}" href="images/${coverAsset.fileName}" media-type="${coverAsset.mediaType}" properties="cover-image"/>`,
        ]
      : []),
    ...(backCoverAsset
      ? [
          `<item id="${backCoverAsset.id}" href="images/${backCoverAsset.fileName}" media-type="${backCoverAsset.mediaType}"/>`,
        ]
      : []),
    ...enrichedImages.map(
      (image) =>
        `<item id="${image.id}" href="images/${image.fileName}" media-type="${image.mediaType}"/>`
    ),
  ].join("\n    ");

  const spineItems = spineDocs
    .map((chapter) => `<itemref idref="${chapter.id}"/>`)
    .join("\n    ");

  const contentOpf = `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" unique-identifier="book-id" version="3.0" xml:lang="${escapeXml(
    epubMetadata.language
  )}">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="book-id">${escapeXml(identifier)}</dc:identifier>
    <dc:title>${escapeXml(title)}</dc:title>
    <dc:language>${escapeXml(epubMetadata.language)}</dc:language>
    <dc:creator>${escapeXml(epubMetadata.creator)}</dc:creator>
    <dc:publisher>${escapeXml(epubMetadata.publisher)}</dc:publisher>
    <dc:subject>${escapeXml(epubMetadata.subject)}</dc:subject>
    <dc:rights>${escapeXml(epubMetadata.rights)}</dc:rights>
    <dc:description>${escapeXml(epubMetadata.description || title)}</dc:description>
    <dc:date>${escapeXml(createdDate)}</dc:date>
    ${coverAsset ? '<meta name="cover" content="cover-image"/>' : ""}
    <meta property="dcterms:modified">${modifiedDate}</meta>
  </metadata>
  <manifest>
    ${manifestItems}
  </manifest>
  <spine toc="ncx">
    ${spineItems}
  </spine>
</package>`;

  const zip = new JSZip();
  zip.file("mimetype", "application/epub+zip", { compression: "STORE" });
  zip.folder("META-INF")?.file("container.xml", CONTAINER_XML);

  const oebpsFolder = zip.folder("OEBPS");
  oebpsFolder?.file("content.opf", contentOpf);
  oebpsFolder?.file("nav.xhtml", navXhtml);
  oebpsFolder?.file("toc.ncx", tocNcx);

  const textFolder = oebpsFolder?.folder("text");
  spineDocs.forEach((chapter) => {
    textFolder?.file(`${chapter.id}.xhtml`, chapter.xhtml);
  });

  const imagesFolder = oebpsFolder?.folder("images");
  if (coverAsset && coverBlob) {
    imagesFolder?.file(coverAsset.fileName, coverBlob);
  }
  if (backCoverAsset && backCoverBlob) {
    imagesFolder?.file(backCoverAsset.fileName, backCoverBlob);
  }
  enrichedImages.forEach((image) => {
    imagesFolder?.file(image.fileName, image.blob);
  });

  const epubBlob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/epub+zip",
  });

  const fileNameBase = getFolderNameFromPath(path);
  const exportedAt = new Date()
    .toISOString()
    .replace(/[:.]/g, "-")
    .replace("T", "_")
    .replace("Z", "");
  saveAs(epubBlob, `${slugify(fileNameBase)}-${exportedAt}.epub`);
};

export const fetchReadmeMarkdownForRendering = async ({
  path,
  sourceMarkdown,
  githubLogin = "",
}) => {
  if (!isPathAccessible(path, githubLogin)) {
    throw new Error("Unable to fetch markdown for this path.");
  }
  if (typeof sourceMarkdown === "string" && sourceMarkdown.trim()) {
    return sourceMarkdown;
  }
  return fetchMarkdown(path, githubLogin);
};
